'use client';

import { type ComponentType } from 'react';

import { Box } from '@mui/material';

import { useVWOExperiment } from '@/hooks/useVWOExperiment/useVWOExperiment';
import { componentMap as availableComponents } from '@/components/ComponentResolver/componentMap';
import { Maybe, ExperimentContainerVariationItemsCollection, ExperimentVariationContentsItem } from '@/types/generated';

const CONTROL_VARIATION_ID = '1';

export type ExperimentType = 'Client-Side' | 'Server-Side';

type ChildTypeName = keyof typeof availableComponents;

export interface IExperimentContainerProps {
	/** "Client-Side" uses VWO SmartCode (experimentId), "Server-Side" uses VWO FME (featureFlagKey). */
	experimentType?: Maybe<string>;
	/** VWO SmartCode experiment ID. Required when experimentType is "Client-Side". */
	experimentId: Maybe<string>;
	/** VWO FME feature flag key. Required when experimentType is "Server-Side". */
	featureFlagKey?: Maybe<string>;
	/**
	 * Server-resolved variation identifier (from `getFeatureFlags`).
	 * Required when experimentType is "Server-Side". Bypasses the client-side
	 * VWO hook and selects the active variation item directly.
	 */
	resolvedVariation?: Maybe<string>;
	variationItemsCollection: Maybe<ExperimentContainerVariationItemsCollection>;
}

function validateProps(props: Readonly<IExperimentContainerProps>): void {
	const { experimentType, experimentId, featureFlagKey, resolvedVariation } = props;

	if (!experimentType) {
		if (!experimentId) {
			console.warn(
				'ExperimentContainer: experimentType is not set and experimentId is missing. Defaulting to Client-Side mode.'
			);
		}
		return;
	}

	if (experimentType === 'Client-Side') {
		if (!experimentId) {
			console.warn('ExperimentContainer: Client-Side mode requires experimentId.');
		}
		if (featureFlagKey) {
			console.warn(
				'ExperimentContainer: featureFlagKey is set but experimentType is "Client-Side". featureFlagKey will be ignored.'
			);
		}
	} else if (experimentType === 'Server-Side') {
		if (!featureFlagKey) {
			console.warn('ExperimentContainer: Server-Side mode requires featureFlagKey.');
		}
		if (!resolvedVariation) {
			console.warn(
				'ExperimentContainer: Server-Side mode requires resolvedVariation to be passed from the server.'
			);
		}
		if (experimentId) {
			console.warn(
				'ExperimentContainer: experimentId is set but experimentType is "Server-Side". experimentId will be ignored.'
			);
		}
	} else {
		console.warn(
			`ExperimentContainer: Unknown experimentType "${experimentType}". Expected "Client-Side" or "Server-Side".`
		);
	}
}

function findVariation(
	items: Array<Maybe<ExperimentVariationContentsItem>>,
	variationId: string | null
): Maybe<ExperimentVariationContentsItem> {
	if (!variationId) return null;
	return items.find((item) => item?.variationId === variationId) ?? null;
}

function findControlVariation(
	items: Array<Maybe<ExperimentVariationContentsItem>>
): Maybe<ExperimentVariationContentsItem> {
	return findVariation(items, CONTROL_VARIATION_ID) ?? items[0] ?? null;
}

function useResolvedVariation(
	experimentType: Maybe<string> | undefined,
	experimentId: Maybe<string>,
	resolvedVariation: Maybe<string> | undefined
): { variationId: string | null; isReady: boolean } {
	const isServerSide = experimentType === 'Server-Side';
	const hookArg: Maybe<string> = isServerSide ? null : experimentId ?? null;
	const clientResult = useVWOExperiment(hookArg);

	if (isServerSide) {
		return { variationId: resolvedVariation ?? null, isReady: true };
	}

	return clientResult;
}

export default function ExperimentContainer(props: Readonly<IExperimentContainerProps>) {
	const { experimentType, experimentId, featureFlagKey, resolvedVariation, variationItemsCollection } = props;

	if (process.env.NODE_ENV !== 'production') {
		validateProps(props);
	}

	const { variationId, isReady } = useResolvedVariation(experimentType, experimentId, resolvedVariation);

	const variations = variationItemsCollection?.items ?? [];

	if (!variations.length) {
		return <Box component="section" data-testid="empty-experiment-container" />;
	}

	const activeVariation =
		isReady && variationId
			? findVariation(variations, variationId) ?? findControlVariation(variations)
			: findControlVariation(variations);

	const contents = activeVariation?.contentsCollection?.items ?? [];

	const identifier = experimentType === 'Server-Side' ? featureFlagKey ?? 'unknown' : experimentId ?? 'unknown';

	return (
		<Box
			component="section"
			data-testid={`ExperimentContainer_${identifier}`}
			data-experiment-type={experimentType}
			data-experiment-id={experimentType !== 'Server-Side' ? experimentId : undefined}
			data-feature-flag-key={experimentType === 'Server-Side' ? featureFlagKey : undefined}
			data-variation-id={
				isReady
					? variationId ?? activeVariation?.variationId ?? CONTROL_VARIATION_ID
					: activeVariation?.variationId ?? CONTROL_VARIATION_ID
			}
		>
			{contents.map((item, index) => {
				if (!item?.__typename) return null;

				const Component = availableComponents[item.__typename as ChildTypeName] as
					| ComponentType<Record<string, unknown>>
					| undefined;
				if (!Component) return null;

				return <Component key={item.sys?.id ?? index} {...(item as Record<string, unknown>)} />;
			})}
		</Box>
	);
}
